<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="signoff-modal dashboard-viz" @click.stop>
      <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="$emit('close')" />

      <header class="signoff-header">
        <h2>{{ logbook.label }}</h2>
        <p class="signoff-subtitle">{{ logbook.product_name }} &middot; {{ logbook.project_name }}</p>
      </header>

      <div class="kpi-row">
        <stat-tile label="Total tasks" :value="logbook.total" icon="tasks" />
        <stat-tile label="Completed" :value="logbook.completed" icon="circle-check" tone="good" />
        <stat-tile label="Signed off" :value="logbook.signedOff" icon="pen-nib" tone="accent" />
        <stat-tile label="Revoked" :value="logbook.revoked" icon="ban" tone="critical" />
      </div>

      <stacked-bar title="Entry completion" :segments="segments" empty-text="No entries recorded yet." />

      <p v-if="skippedCount > 0" class="skipped-note">
        {{ skippedCount }} {{ skippedCount === 1 ? 'entry has' : 'entries have' }} an unparsable sign-off date and
        were excluded from the chart below.
      </p>

      <div class="chart-toolbar">
        <granularity-toggle
          v-model="trendGranularity"
          :options="granularityOptions"
          aria-label="Sign-off timeline granularity"
        />
      </div>
      <line-chart
        title="Sign-offs over time"
        :series="trendSeries"
        :buckets="trend.buckets"
        :note="trend.truncated ? `Showing the last ${trend.buckets.length} periods` : ''"
        empty-text="No sign-off activity recorded yet for this logbook."
      />
    </div>
  </div>
</template>

<script>
import StatTile from './StatTile.vue';
import StackedBar from './StackedBar.vue';
import LineChart from './LineChart.vue';
import GranularityToggle from './GranularityToggle.vue';
import { buildTrend, GRANULARITY_OPTIONS } from './signOffTrend.js';

export default {
  name: 'LogbookSignOffModal',
  components: {
    StatTile,
    StackedBar,
    LineChart,
    GranularityToggle,
  },
  props: {
    logbook: { type: Object, required: true },
    events: { type: Array, default: () => [] }, // [{ date: Date, type, taskLabel, instructorName }]
    segments: { type: Array, default: () => [] },
    skippedCount: { type: Number, default: 0 },
  },
  emits: ['close'],
  data() {
    return {
      trendGranularity: 'week',
      granularityOptions: GRANULARITY_OPTIONS,
    };
  },
  computed: {
    trendSeries() {
      return [
        { key: 'completed', label: 'Trainee completed', colorVar: '--series-1' },
        { key: 'signedOff', label: 'Instructor signed off', colorVar: '--series-2' },
      ];
    },
    trend() {
      const records = this.events.map((e) => ({
        date: e.date,
        seriesKey: e.type === 'Trainee completed' ? 'completed' : 'signedOff',
      }));
      return buildTrend(records, this.trendGranularity);
    },
  },
};
</script>

<style scoped>
.dashboard-viz {
  color-scheme: light;
  --surface-1:      #fcfcfb;
  --page-plane:     #f2f2ef;
  --text-primary:   #0b0b0b;
  --text-secondary: #52514e;
  --text-muted:     #898781;
  --gridline:       #e1e0d9;
  --baseline:       #c3c2b7;
  --series-1:       #2a78d6;
  --series-2:       #eb6834;
  --status-good:      #0ca30c;
  --status-warning:   #fab219;
  --status-critical:  #d03b3b;
  --border: rgba(11, 11, 11, 0.10);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(11, 11, 11, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.signoff-modal {
  position: relative;
  background: var(--surface-1, #fff);
  border-radius: 10px;
  padding: 28px 32px 32px;
  width: min(760px, 100%);
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(11, 11, 11, 0.25);
}

.window-close-icon {
  position: absolute;
  top: 16px;
  right: 18px;
  cursor: pointer;
  color: var(--text-muted, #898781);
  font-size: 18px;
}

.window-close-icon:hover {
  color: var(--text-primary, #0b0b0b);
}

.signoff-header {
  margin-bottom: 20px;
  padding-right: 24px;
}

.signoff-header h2 {
  margin: 0 0 4px;
  font-size: 20px;
  color: var(--text-primary);
  text-align: left;
}

.signoff-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  text-align: left;
}

.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}

.signoff-modal > figure {
  margin-top: 16px;
}

.skipped-note {
  font-size: 12px;
  color: var(--text-muted);
  margin: 14px 0 16px;
}

.chart-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.chart-toolbar + figure {
  margin-top: 8px;
}
</style>
